import React, { useState, useEffect } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import { 
  BarChart3, 
  Users, 
  Building, 
  CreditCard, 
  MessageSquare, 
  Bell, 
  Settings,
  Briefcase
} from 'lucide-react';

// Admin Components
import AdminHeader from '@/components/admin/AdminHeader';
import AdminDashboard from '@/components/admin/AdminDashboard';
import UsersManagement from '@/components/admin/UsersManagement';
import PropertiesManagement from '@/components/admin/PropertiesManagement';
import SubscriptionsManagement from '@/components/admin/SubscriptionsManagement';
import ManagementRequests from '@/components/admin/ManagementRequests';
import NotificationsCenter from '@/components/admin/NotificationsCenter';
import AdminDebugPanel from '@/components/admin/AdminDebugPanel';
import RouteGuard from '@/components/RouteGuard';
import PropertyCard from '@/components/PropertyCard';
import { Badge } from '@/components/ui/badge';
import SiteSettingsManagement from '@/components/admin/SiteSettingsManagement';
import ServiceProvidersManagement from '@/components/admin/ServiceProvidersManagement';

const AdminContent = () => {
  const [selectedTab, setSelectedTab] = useState('dashboard');
  
  // State for data
  const [users, setUsers] = useState([]);
  const [properties, setProperties] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [managementRequests, setManagementRequests] = useState([]);
  
  // Authentication state
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  
  const { toast } = useToast();
  const navigate = useNavigate();

  // Check authentication and admin status
  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      // Check for admin session in localStorage
      const adminSession = localStorage.getItem('admin_session');
      if (adminSession) {
        const adminData = JSON.parse(adminSession);
        setIsAuthenticated(true);
        setIsAdmin(true);
        setLoading(false);
        return;
      }

      // Check Supabase Auth for regular users
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate('/login');
        return;
      }

      setIsAuthenticated(true);

      // Check if user is admin in kanfadmin table
      const { data: adminData, error } = await supabase
        .from('kanfadmin')
        .select('*')
        .eq('email', session.user.email)
        .eq('is_active', true)
        .maybeSingle();

      if (adminData && !error) {
        setIsAdmin(true);
        setLoading(false);
        return;
      }

      // If not admin, redirect to home
      toast({
        title: "غير مصرح",
        description: "ليس لديك صلاحية للوصول إلى هذه الصفحة",
        variant: "destructive"
      });
      navigate('/');
    } catch (error) {
      console.error('Auth check error:', error);
      navigate('/login');
    }
  };

  // Load data on component mount
  useEffect(() => {
    if (isAdmin && isAuthenticated) {
      loadDashboardData();
    }
  }, [isAdmin, isAuthenticated]);

  const loadDashboardData = async () => {
    try {
      await Promise.all([
        loadUsers(),
        loadProperties(),
        loadSubscriptions(),
        loadManagementRequests()
      ]);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      toast({
        title: "خطأ",
        description: "حدث خطأ في تحميل البيانات",
        variant: "destructive"
      });
    }
  };

  const loadUsers = async () => {
    try {
      // Load landlords
      const { data: landlords, error: landlordsError } = await supabase
        .from('landlords')
        .select('*');
      
      if (landlordsError) throw landlordsError;

      // Load tenants
      const { data: tenants, error: tenantsError } = await supabase
        .from('tenants')
        .select('*');
      
      if (tenantsError) throw tenantsError;

      // Load service providers
      const { data: serviceProviders, error: serviceProvidersError } = await supabase
        .from('service_providers')
        .select('*');
      
      if (serviceProvidersError) throw serviceProvidersError;

      // Combine and format users
      const combinedUsers = [
        ...landlords.map(landlord => ({
          id: landlord.id,
          name: landlord.full_name,
          email: landlord.email,
          phone: landlord.phone,
          type: 'مالك',
          status: landlord.is_active ? 'نشط' : 'معطل',
          joinDate: new Date(landlord.created_at).toLocaleDateString('en-GB'),
          propertiesCount: landlord.total_properties || 0,
          userId: landlord.user_id,
          isActive: landlord.is_active
        })),
        ...tenants.map(tenant => ({
          id: tenant.id,
          name: tenant.full_name,
          email: tenant.email,
          phone: tenant.phone,
          type: 'باحث',
          status: tenant.is_active ? 'نشط' : 'معطل',
          joinDate: new Date(tenant.created_at).toLocaleDateString('en-GB'),
          propertiesCount: 0,
          userId: tenant.user_id,
          isActive: tenant.is_active
        })),
        ...(serviceProviders || []).map(provider => ({
          id: provider.id,
          name: provider.full_name,
          email: provider.email,
          phone: provider.phone,
          type: 'صاحب خدمة',
          status: provider.is_active ? 'نشط' : 'معطل',
          joinDate: new Date(provider.created_at).toLocaleDateString('en-GB'),
          propertiesCount: 0,
          userId: provider.user_id,
          isActive: provider.is_active
        }))
      ];
      
      setUsers(combinedUsers);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const loadProperties = async () => {
    try {
      const { data, error } = await supabase
        .from('properties')
        .select(`
          *,
          landlords(full_name),
          property_images(url, is_main)
        `)
        .order('created_at', { ascending: false });
      
      if (error) throw error;

      const formattedProperties = data.map(property => ({
        ...property,
        ownerName: property.landlords?.full_name || 'غير محدد',
        mainImage: property.property_images?.find(img => img.is_main)?.url || 
                  property.property_images?.[0]?.url
      }));
      
      setProperties(formattedProperties);
    } catch (error) {
      console.error('Error loading properties:', error);
    }
  };

  const loadSubscriptions = async () => {
    try {
      const { data, error } = await supabase
        .from('subscriptions')
        .select(`
          *,
          landlords(full_name, total_properties)
        `);
      
      if (error) throw error;

      const formattedSubscriptions = data.map(subscription => ({
        id: subscription.id,
        ownerName: subscription.landlords?.full_name || 'غير محدد',
        plan: 'أساسي',
        price: subscription.price,
        startDate: new Date(subscription.start_date).toLocaleDateString('en-GB'),
        endDate: new Date(subscription.end_date).toLocaleDateString('en-GB'),
        status: subscription.status === 'active' ? 'نشط' : 'منتهي',
        propertiesCount: subscription.landlords?.total_properties || 0
      }));
      
      setSubscriptions(formattedSubscriptions);
    } catch (error) {
      console.error('Error loading subscriptions:', error);
    }
  };

  const loadManagementRequests = async () => {
    try {
      const { data, error } = await supabase
        .from('property_management_requests')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;

      setManagementRequests(data);
    } catch (error) {
      console.error('Error loading management requests:', error);
    }
  };

  // User Management Functions
  const toggleUserStatus = async (userId: string, currentIsActive: boolean, userType: string) => {
    try {
      const newStatus = !currentIsActive;
      
      let error = null;
      if (userType === 'مالك') {
        const result = await supabase
          .from('landlords')
          .update({ is_active: newStatus })
          .eq('user_id', userId);
        error = result.error;
      } else if (userType === 'صاحب خدمة') {
        const result = await supabase
          .from('service_providers')
          .update({ is_active: newStatus })
          .eq('user_id', userId);
        error = result.error;
      } else {
        const result = await supabase
          .from('tenants')
          .update({ is_active: newStatus })
          .eq('user_id', userId);
        error = result.error;
      }
      
      if (error) {
        throw error;
      }

      await loadUsers();
      
      toast({
        title: "تم التحديث",
        description: `تم ${newStatus ? 'تفعيل' : 'إلغاء تفعيل'} الحساب بنجاح`
      });
    } catch (error: any) {
      toast({
        title: "خطأ",
        description: "حدث خطأ في تحديث حالة المستخدم: " + error.message,
        variant: "destructive"
      });
    }
  };

  const deleteUser = async (userId: string, userType: string) => {
    if (!confirm('هل أنت متأكد من حذف هذا المستخدم؟ سيتم حذف جميع البيانات المرتبطة به.')) return;
    
    try {
      
      // حذف المستخدم من الجدول المناسب أولاً
      let deleteError = null;
      if (userType === 'مالك') {
        const result = await supabase
          .from('landlords')
          .delete()
          .eq('user_id', userId);
        deleteError = result.error;
      } else if (userType === 'صاحب خدمة') {
        const result = await supabase
          .from('service_providers')
          .delete()
          .eq('user_id', userId);
        deleteError = result.error;
      } else {
        const result = await supabase
          .from('tenants')
          .delete()
          .eq('user_id', userId);
        deleteError = result.error;
      }
      
      if (deleteError) {
        throw deleteError;
      }

      // محاولة حذف المستخدم من auth.users باستخدام الدالة الإدارية
      try {
        const { data: deleteAuthResult, error: authDeleteError } = await supabase
          .rpc('admin_delete_user', { user_uuid: userId });
        
        // Silent handling of auth deletion
      } catch (authError) {
        // Silent handling of auth deletion errors
      }

      await loadUsers();
      
      toast({
        title: "تم الحذف",
        description: "تم حذف المستخدم بنجاح من النظام"
      });
    } catch (error: any) {
      console.error('Error deleting user:', error);
      toast({
        title: "خطأ",
        description: "حدث خطأ في حذف المستخدم: " + error.message,
        variant: "destructive"
      });
    }
  };

  // Property Management Functions
  const togglePropertyStatus = async (propertyId, currentStatus) => {
    try {
      const { error } = await supabase
        .from('properties')
        .update({ is_active: !currentStatus })
        .eq('id', propertyId);
      
      if (error) throw error;

      await loadProperties();
      
      toast({
        title: "تم التحديث",
        description: `تم ${!currentStatus ? 'تفعيل' : 'إلغاء تفعيل'} العقار بنجاح`
      });
    } catch (error) {
      console.error('Error toggling property status:', error);
      toast({
        title: "خطأ",
        description: "حدث خطأ في تحديث حالة العقار",
        variant: "destructive"
      });
    }
  };

  const deleteProperty = async (propertyId) => {
    if (!confirm('هل أنت متأكد من حذف هذا العقار؟')) return;
    
    try {
      const { error } = await supabase
        .from('properties')
        .delete()
        .eq('id', propertyId);
      
      if (error) throw error;

      await loadProperties();
      
      toast({
        title: "تم الحذف",
        description: "تم حذف العقار بنجاح"
      });
    } catch (error) {
      console.error('Error deleting property:', error);
      toast({
        title: "خطأ",
        description: "حدث خطأ في حذف العقار",
        variant: "destructive"
      });
    }
  };

  // Subscription Management
  const activateSubscription = async (landlordId) => {
    try {
      const endDate = new Date();
      endDate.setMonth(endDate.getMonth() + 1);
      
      const { error } = await supabase
        .from('subscriptions')
        .insert([{
          landlord_id: landlordId,
          end_date: endDate.toISOString().split('T')[0],
          status: 'active'
        }]);
      
      if (error) throw error;

      // Activate landlord account
      const { error: landlordError } = await supabase
        .from('landlords')
        .update({ is_active: true })
        .eq('id', landlordId);
      
      if (landlordError) throw landlordError;

      await loadSubscriptions();
      await loadUsers();
      
      toast({
        title: "تم التفعيل",
        description: "تم تفعيل اشتراك المالك بنجاح"
      });
    } catch (error) {
      console.error('Error activating subscription:', error);
      toast({
        title: "خطأ",
        description: "حدث خطأ في تفعيل الاشتراك",
        variant: "destructive"
      });
    }
  };

  // Management Requests
  const updateRequestStatus = async (requestId, status) => {
    try {
      const { error } = await supabase
        .from('property_management_requests')
        .update({ status })
        .eq('id', requestId);
      
      if (error) throw error;

      await loadManagementRequests();
      
      toast({
        title: "تم التحديث",
        description: `تم ${status === 'approved' ? 'قبول' : 'رفض'} الطلب بنجاح`
      });
    } catch (error) {
      console.error('Error updating request status:', error);
      toast({
        title: "خطأ",
        description: "حدث خطأ في تحديث حالة الطلب",
        variant: "destructive"
      });
    }
  };

  // Notifications
  const sendNotification = async (type, title, message, recipientId) => {
    try {
      const notificationData: any = {
        recipient_type: type,
        title,
        message,
        notification_type: 'admin_message',
        is_sent: true,
        sent_at: new Date().toISOString()
      };

      // للرسائل الفردية أو المجموعات المحددة
      if ((type === 'individual' || type === 'landlord' || type === 'tenant') && recipientId) {
        notificationData.recipient_id = recipientId;
      }

      const { error } = await supabase
        .from('admin_notifications')
        .insert([notificationData]);
      
      if (error) {
        console.error('خطأ في إرسال الإشعار:', error);
        throw error;
      }
      
      toast({
        title: "تم الإرسال",
        description: "تم إرسال الإشعار بنجاح"
      });
    } catch (error) {
      console.error('Error sending notification:', error);
      toast({
        title: "خطأ",
        description: "حدث خطأ في إرسال الإشعار: " + error.message,
        variant: "destructive"
      });
    }
  };

  const sendMessageToUser = async (recipientId, title, message) => {
    await sendNotification('individual', title, message, recipientId);
  };

  const handleLogout = async () => {
    // Remove admin session
    localStorage.removeItem('admin_session');
    
    // Sign out from Supabase if logged in
    try {
      await supabase.auth.signOut();
    } catch (error) {
      console.log('No active supabase session');
    }
    
    navigate('/login');
    toast({
      title: "تم تسجيل الخروج",
      description: "تم تسجيل خروجك بنجاح"
    });
  };

  const handleEditProperty = (propertyId: string) => {
    // TODO: Implement property editing
    toast({
      title: "قريباً",
      description: "ميزة تعديل العقار ستكون متاحة قريباً"
    });
  };

  const handleViewPropertyRequests = (propertyId: string) => {
    // TODO: Implement viewing property requests
    toast({
      title: "قريباً", 
      description: "عرض طلبات العقار ستكون متاحة قريباً"
    });
  };

  // Loading and authentication checks
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-slate-600">جاري التحقق من الصلاحيات...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated || !isAdmin) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-900 mb-4">غير مصرح بالوصول</h1>
          <p className="text-slate-600 mb-8">ليس لديك صلاحية للوصول إلى هذه الصفحة</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <AdminHeader onLogout={handleLogout} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs value={selectedTab} onValueChange={setSelectedTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-8 lg:grid-cols-8 h-12">
          <TabsTrigger value="dashboard" className="flex items-center gap-2 text-xs lg:text-sm">
            <BarChart3 className="h-4 w-4" />
            الإحصائيات
          </TabsTrigger>
          <TabsTrigger value="users" className="flex items-center gap-2 text-xs lg:text-sm">
            <Users className="h-4 w-4" />
            المستخدمين
          </TabsTrigger>
          <TabsTrigger value="properties" className="flex items-center gap-2 text-xs lg:text-sm">
            <Building className="h-4 w-4" />
            العقارات
          </TabsTrigger>
          <TabsTrigger value="subscriptions" className="flex items-center gap-2 text-xs lg:text-sm">
            <CreditCard className="h-4 w-4" />
            الاشتراكات
          </TabsTrigger>
          <TabsTrigger value="service-providers" className="flex items-center gap-2 text-xs lg:text-sm">
            <Briefcase className="h-4 w-4" />
            أصحاب الخدمات
          </TabsTrigger>
          <TabsTrigger value="requests" className="flex items-center gap-2 text-xs lg:text-sm">
            <MessageSquare className="h-4 w-4" />
            الطلبات
          </TabsTrigger>
          <TabsTrigger value="notifications" className="flex items-center gap-2 text-xs lg:text-sm">
            <Bell className="h-4 w-4" />
            الإشعارات
          </TabsTrigger>
          <TabsTrigger value="settings" className="flex items-center gap-2 text-xs lg:text-sm">
            <Settings className="h-4 w-4" />
            الإعدادات
          </TabsTrigger>
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard">
          <AdminDashboard />
        </TabsContent>

        {/* Users Management Tab */}
        <TabsContent value="users">
          <UsersManagement 
            users={users}
            onToggleUserStatus={toggleUserStatus}
            onDeleteUser={deleteUser}
            onSendMessage={sendMessageToUser}
          />
        </TabsContent>

        {/* Properties Management Tab */}
        <TabsContent value="properties">
          <PropertiesManagement />
        </TabsContent>

        {/* Subscriptions Management Tab */}
        <TabsContent value="subscriptions">
          <SubscriptionsManagement />
        </TabsContent>

        {/* Service Providers Management Tab */}
        <TabsContent value="service-providers">
          <ServiceProvidersManagement />
        </TabsContent>

        {/* Management Requests Tab */}
        <TabsContent value="requests">
          <ManagementRequests 
            requests={managementRequests}
            onUpdateRequestStatus={updateRequestStatus}
          />
        </TabsContent>

        {/* Notifications Tab */}
        <TabsContent value="notifications">
          <NotificationsCenter 
            onSendNotification={sendNotification}
            users={users}
          />
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings">
          <SiteSettingsManagement />
        </TabsContent>
      </Tabs>
      </div>
    </div>
  );
};

const Admin = () => {
  return (
    <RouteGuard requiredUserType="admin" redirectTo="/login">
      <AdminContent />
    </RouteGuard>
  );
};

export default Admin;
